<template>
  <div
    class="lista-espera-overlay"
    :class="{ 'landing-experiencia': !!styleVars }"
    :style="styleVars || undefined"
    @click.self="closeModal">
    <div class="lista-espera-modal rounded-12 p-30 p-m-20">
      <div class="d-flex align-items-start justify-content-between mb-10">
        <span class="h6 semibold title-landing pr-10">{{ $t('modalUnirseHabitacion.titulo') }}</span>
        <button type="button" class="lista-espera-close" @click="closeModal">
          <CustomIcon :icon="crossIcon" size="16px" class="text-landing" />
        </button>
      </div>

      <span class="p text-landing mb-20 d-block">{{ $t('modalUnirseHabitacion.subtitulo') }}</span>

      <div class="lista-espera-field mb-15">
        <label class="small medium text-landing">{{ $t('input.field.nombreApellidos') }}</label>
        <div v-for="error in nombreApellidosFormErrors" :key="error" class="sub text-red">
          {{ error }}
        </div>
        <input v-model="nombreApellidos" type="text" class="lista-espera-input p fw-normal text-landing" />
      </div>

      <div class="lista-espera-field mb-15">
        <label class="small medium text-landing">{{ $t('general.email') }}</label>
        <div v-for="error in emailFormErrors" :key="error" class="sub text-red">
          {{ error }}
        </div>
        <input
          v-model.trim="email"
          type="email"
          class="lista-espera-input p fw-normal text-landing"
          :placeholder="$t('modalUnirseHabitacion.placeholderEmail')" />
      </div>

      <div class="lista-espera-field mb-15">
        <label class="small medium text-landing">{{ $t('input.field.telefono') }}</label>
        <div v-for="error in telefonoFormErrors" :key="error" class="sub text-red">
          {{ error }}
        </div>
        <div class="lista-espera-telefono">
          <InputTelefono
            v-model="telefono"
            :tipo="TIPOS_TELEFONO.TIPO_A"
            :has-error="telefonoFormErrors.length > 0"
            :placeholder="$t('modalUnirseHabitacion.placeholderTelefono')"
            show-prefijo-code />
        </div>
      </div>

      <div class="lista-espera-field mb-15">
        <label class="small medium text-landing">{{ $t('invitacionGrupo.plazas') }}</label>
        <div v-for="error in plazasFormErrors" :key="error" class="sub text-red">
          {{ error }}
        </div>
        <div class="lista-espera-plazas">
          <input
            v-model.number="plazas"
            type="number"
            min="0"
            autocomplete="off"
            class="lista-espera-plazas-input p fw-normal text-landing"
            @input="normalizePlazas" />
          <div class="lista-espera-plazas-controls">
            <button
              type="button"
              class="lista-espera-plazas-btn"
              :class="{ 'opacity-50': plazas <= PLAZAS_MIN }"
              @click="decrementPlazas">
              <CustomIcon :icon="minusIcon" size="16px" class="text-landing" />
            </button>
            <span class="lista-espera-plazas-divider" />
            <button type="button" class="lista-espera-plazas-btn" @click="incrementPlazas">
              <CustomIcon :icon="plusIcon" size="16px" class="text-landing" />
            </button>
          </div>
        </div>
      </div>

      <div class="lista-espera-field mb-30">
        <label class="small medium text-landing">
          {{ $t('input.field.observaciones') }}
          <i class="fw-normal">({{ $t('general.opcional') }})</i>
        </label>
        <textarea
          v-model.trim="observaciones"
          class="lista-espera-input lista-espera-textarea p fw-normal text-landing" />
      </div>

      <div class="d-flex justify-content-end gap-20">
        <MyGocuButton secondary :label="$t('botones.cancelar')" @click="closeModal" />
        <MyGocuButton
          primary
          :label="$t('botones.enviar')"
          :disabled="isViajeBorrador({ estado: estadoViaje })"
          @click="submit" />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { createToaster } from '@meforma/vue-toaster';
  import CustomIcon from '@/components/CustomIcon.vue';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import InputTelefono from '@/components/inputs/InputTelefono.vue';
  import useFormValidator from '@/helpers/useFormValidator';
  import useRegexConstants from '@/helpers/useRegexConstants';
  import { CONFLICT } from '@/services/utils/HttpStatus';
  import ViajesGrupoApiService from '@/services/api/ViajesGrupoApiService';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { TIPOS_TELEFONO } from '@/constants/inputsConstants';
  import { isViajeBorrador } from '@/services/utils/ViajeUtils';
  import crossIcon from '@/assets/icons/raw/cerrar.svg';
  import minusIcon from '@/assets/icons/raw/minus.svg';
  import plusIcon from '@/assets/icons/raw/plus.svg';
  import arrowDownIcon from '@/assets/icons/arrow_down.svg';

  const props = defineProps({
    viajeId: {
      type: String,
      required: true,
    },
    alojamientoId: {
      type: [Number, String],
      default: null,
    },
    serviciosDisponibles: {
      type: Boolean,
      default: false,
    },
    styleVars: {
      type: Object,
      default: null,
    },
    estadoViaje: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits(['close']);

  const PLAZAS_MIN = 0;
  const arrowDownMask = `url(${arrowDownIcon})`;

  const { validateInput } = useFormValidator();
  const { REGEX_VALID_NOMBRE } = useRegexConstants();
  const { t } = useI18n();
  const loadingStore = useLoadingStore();

  const toaster = createToaster({ position: 'top-right' });

  const nombreApellidos = ref('');
  const email = ref('');
  const telefono = ref('');
  const plazas = ref(PLAZAS_MIN);
  const observaciones = ref('');
  const nombreApellidosFormErrors = ref([]);
  const emailFormErrors = ref([]);
  const telefonoFormErrors = ref([]);
  const plazasFormErrors = ref([]);

  function telefonoFormat() {
    return telefono.value.replace(/\D/g, '');
  }

  function normalizePlazas() {
    if (Number.isNaN(plazas.value) || plazas.value < PLAZAS_MIN) {
      plazas.value = PLAZAS_MIN;
    }
  }

  function incrementPlazas() {
    plazas.value += 1;
  }

  function decrementPlazas() {
    if (plazas.value <= PLAZAS_MIN) {
      return;
    }
    plazas.value -= 1;
  }

  function buildVGListaEsperaDTO() {
    return {
      viajeId: props.viajeId,
      alojamientoId: props.alojamientoId ?? null,
      serviciosDisponibles: props.serviciosDisponibles,
      nombre: nombreApellidos.value,
      email: email.value,
      telefono: telefonoFormat(),
      plazas: plazas.value,
      observaciones: observaciones.value,
    };
  }

  function validateForm() {
    nombreApellidosFormErrors.value = validateInput(
      nombreApellidos.value,
      'text',
      true,
      50,
      null,
      REGEX_VALID_NOMBRE
    );
    emailFormErrors.value = validateInput(email.value, 'email', true);
    telefonoFormErrors.value = validateInput(telefonoFormat(), 'tel', true, null, 9);
    plazasFormErrors.value = validateInput(String(plazas.value), 'text', true);

    return (
      nombreApellidosFormErrors.value.length === 0 &&
      emailFormErrors.value.length === 0 &&
      telefonoFormErrors.value.length === 0 &&
      plazasFormErrors.value.length === 0
    );
  }

  async function submit() {
    if (isViajeBorrador({ estado: props.estadoViaje }) || !validateForm()) {
      return;
    }

    try {
      loadingStore.show();
      await ViajesGrupoApiService.agregarListaEspera(buildVGListaEsperaDTO());
      toaster.success(t('general.listaEsperaSuccess'));
      closeModal();
    } catch (error) {
      console.log(error);
      const errorMsg =
        error.response && error.response.status === CONFLICT.code ? error.response.data : t('general.listaEsperaError');
      toaster.error(errorMsg);
    } finally {
      loadingStore.hide();
    }
  }

  function closeModal() {
    emit('close');
  }
</script>

<style scoped>
  .lista-espera-overlay {
    position: fixed;
    inset: 0;
    z-index: 1010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background-color: rgba(var(--bs-black-rgb), 0.5);
  }

  .lista-espera-modal {
    width: 600px;
    max-width: 100%;
    max-height: 100%;
    overflow-y: auto;
    background-color: var(--landing-section-background-color, var(--landing-background-color, var(--bs-white)));
    box-shadow: 0 0 1px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.4),
      2px 4px 5px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1),
      4px 6px 20px rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
  }

  .lista-espera-close {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: -5px -10px 0 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  .lista-espera-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .lista-espera-input {
    width: 100%;
    height: 40px;
    padding: 10px;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
    border-radius: 6px;
    background: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.05);
    box-shadow: none;
    text-align: left;
  }

  .lista-espera-input::placeholder {
    color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.5);
  }

  .lista-espera-input:focus {
    outline: none;
    border-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.4);
  }

  .lista-espera-textarea {
    height: 80px;
    resize: none;
  }

  .lista-espera-telefono {
    width: 100%;
  }

  .lista-espera-telefono :deep(.vue-tel-input.input-telefono-tipo-a),
  .lista-espera-telefono :deep(.vue-tel-input.input-telefono-tipo-a:focus-within) {
    display: flex !important;
    align-items: center !important;
    gap: 5px !important;
    height: 40px !important;
    padding: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    color: var(--landing-text-color, var(--bs-primary)) !important;
  }

  .lista-espera-telefono :deep(.vti__dropdown),
  .lista-espera-telefono :deep(.vti__dropdown:hover),
  .lista-espera-telefono :deep(.vti__dropdown.open) {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    width: 84px;
    height: 30px;
    padding: 0;
    border: none;
    overflow: visible;
    background: transparent;
  }

  .lista-espera-telefono :deep(.vti__dropdown.show) {
    overflow: visible;
    max-height: none;
  }

  .lista-espera-telefono :deep(.vti__selection) {
    display: flex;
    align-items: center;
    width: 84px;
    height: 30px;
    gap: 5px;
    overflow: visible;
  }

  .lista-espera-telefono :deep(.vti__flag) {
    flex-shrink: 0;
    width: 20px;
    height: 15px;
    margin: 0 20px 0 -5px;
    border-radius: 0;
    clip-path: circle(7.5px at 50% 50%);
    transform: scale(2);
    transform-origin: left center;
  }

  .lista-espera-telefono :deep(.vti__country-code) {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    width: 28px;
    height: 19px;
    color: var(--landing-text-color, var(--bs-primary));
    font-size: 16px !important;
    line-height: 19px !important;
  }

  .lista-espera-telefono :deep(.vti__dropdown-arrow) {
    flex-shrink: 0;
    display: inline-block;
    width: 16px;
    height: 16px;
    margin: 0 0 0 5px !important;
    border: none;
    font-size: 0;
    line-height: 0;
    color: transparent;
    transform: none;
    background-color: var(--landing-text-color, var(--bs-primary));
    -webkit-mask: v-bind(arrowDownMask) center / 16px 16px no-repeat;
    mask: v-bind(arrowDownMask) center / 16px 16px no-repeat;
  }

  .lista-espera-telefono :deep(input.vti__input) {
    flex: 1 1 0;
    width: 0 !important;
    min-width: 0;
    height: 40px;
    padding: 10px !important;
    margin-left: 15px !important;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2) !important;
    border-radius: 6px !important;
    background: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.05) !important;
    color: var(--landing-text-color, var(--bs-primary)) !important;
    text-align: left !important;
  }

  .lista-espera-telefono :deep(input.vti__input)::placeholder {
    color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.5);
  }

  .lista-espera-plazas {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 170px;
    height: 40px;
    padding: 10px;
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
    border-radius: 6px;
    background: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.05);
  }

  .lista-espera-plazas-input {
    min-width: 0;
    flex: 1;
    padding: 0;
    border: none;
    background: transparent;
    text-align: left;
  }

  .lista-espera-plazas-input:focus {
    outline: none;
  }

  .lista-espera-plazas-controls {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .lista-espera-plazas-divider {
    width: 1px;
    height: 16px;
    background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
  }

  .lista-espera-plazas-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--landing-text-color, var(--bs-primary));
    cursor: pointer;
  }

  .lista-espera-plazas-btn.opacity-50 {
    cursor: not-allowed;
  }

  @media screen and (max-width: 768px) {
    .lista-espera-overlay {
      align-items: flex-end;
      padding: 0;
    }

    .lista-espera-modal {
      width: 100%;
      max-height: 90vh;
      border-radius: 12px 12px 0 0 !important;
    }

    .lista-espera-plazas {
      width: 100%;
    }
  }
</style>
